<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import InstallAppSteps from './InstallAppSteps.vue';
import { useInstallApp } from 'dashboard/composables/useInstallApp';

// TalkRobin: on phones, offer the app as soon as TalkRobin opens (login page
// included). Android gets the browser's install prompt (or menu steps in
// browsers without one), iPhone the Share -> Add to Home Screen steps.
// Hidden for a week after it's closed, and never shown inside the installed
// app or on computers (they have the menu item).
const DISMISSED_KEY = 'talkrobin_install_banner_dismissed_at';
// Served from public/, so it's bound rather than bundled.
const APP_ICON_URL = '/talkrobin/apple-touch-icon-180.png';
const SHOW_AGAIN_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

const { t } = useI18n();
const { canInstall, manualSteps, promptInstall } = useInstallApp();
const steps = ref(null);

const readDismissedAt = () => {
  try {
    return Number(window.localStorage.getItem(DISMISSED_KEY)) || 0;
  } catch {
    return 0;
  }
};
const dismissedAt = ref(readDismissedAt());

const isPhone = window.matchMedia?.('(max-width: 768px)').matches;

const isVisible = computed(
  () =>
    isPhone &&
    canInstall.value &&
    Date.now() - dismissedAt.value > SHOW_AGAIN_AFTER_MS
);

const dismiss = () => {
  dismissedAt.value = Date.now();
  try {
    window.localStorage.setItem(DISMISSED_KEY, String(dismissedAt.value));
  } catch {
    // Storage blocked: hidden for this visit only.
  }
};

const install = async () => {
  if (manualSteps.value) {
    steps.value.open();
    return;
  }
  if (await promptInstall()) dismiss();
};
</script>

<template>
  <div
    v-if="isVisible"
    class="fixed z-50 flex items-center gap-3 p-3 shadow-xl inset-x-3 bottom-3 rounded-2xl bg-n-solid-2 ring-1 ring-n-weak"
    role="dialog"
    :aria-label="t('INSTALL_APP.BANNER.TITLE')"
  >
    <img
      :src="APP_ICON_URL"
      alt=""
      class="rounded-xl size-11 shrink-0"
    />
    <div class="flex-1 min-w-0">
      <p class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('INSTALL_APP.BANNER.TITLE') }}
      </p>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('INSTALL_APP.BANNER.DESCRIPTION') }}
      </p>
    </div>
    <Button
      sm
      :label="
        manualSteps
          ? t('INSTALL_APP.HOW_TO_INSTALL')
          : t('INSTALL_APP.BANNER.INSTALL')
      "
      @click="install"
    />
    <Button
      xs
      ghost
      slate
      icon="i-lucide-x"
      :aria-label="t('INSTALL_APP.BANNER.CLOSE')"
      @click="dismiss"
    />
    <InstallAppSteps ref="steps" :platform="manualSteps || 'apple'" />
  </div>
</template>
